import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {LoadingSpinnerService} from '../../services';
import {TranslateService} from '@ngx-translate/core';
import {ProjectDetails} from '../../models';
import moment from 'moment';
import {FormatEuroCentsDirective} from '../../directives/format-euro-cents.directive';

@Component({
    selector: 'app-project-details',
    templateUrl: './project-details.component.html',
    styleUrls: ['./project-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectDetailsComponent implements OnInit {
    private translateService = inject(TranslateService);
    loadingSpinnerService = inject(LoadingSpinnerService);

    @Input()
    set projectDetails(val: ProjectDetails) {
        this.project = val;
        this.updateDerivedFields();
        this.loadingSpinnerService.hide();
    }

    @Output() isFormValid = new EventEmitter<boolean>();
    public isProcessingThumbnail = false;
    public project: ProjectDetails = null;

    public imageBase64: { image?: string; previewSrc?: string; file?: File };
    public contractType: string;
    public termsAndConditions: string;
    public profitContribution: string;

    get workTypes(): string {
        return this.project.projectAttributes?.workTypes?.map(attr => attr.title)
            .join(', ');
    }

    get buildingClassifications(): string {
        return this.project.projectAttributes?.buildingClassifications?.map(attr => attr.title)
            .join(', ');
    }

    get buildingSubClassifications(): string {
        return this.project.projectAttributes?.buildingSubClassifications?.map(attr => attr.title)
            .join(', ');
    }

    get durationInMonths(): string | number {
        if (this.project.projectInfo?.durationInMonths === 0) {
            return '< 1';
        }
        return this.project.projectInfo.durationInMonths
    }

    get startDate(): string {
        return this.project.projectInfo.workStartsAt;
    }

    get endDate(): string {
        return this.project.projectInfo.workEndsAt;
    }

    ngOnInit(): void {
        if (!this.project) {
            this.loadingSpinnerService.show();
        }

    }

    public getAnswerValue(pitTag: string) {
        const answer = this.project.projectAnalysisAnswers.find(answer => answer.pitTag == pitTag);
        // XXX Naive first iteration of returning the answer
        if (answer) {
            if (answer.answerAsBool !== undefined) {
                return answer.answerAsBool;
            }

            if (answer.answerAsText !== undefined) {
                return answer.answerAsText;
            }

            if (answer.answerAsInteger !== undefined) {
                return answer.answerAsInteger;
            }

            if (answer.answerAsPercentage !== undefined) {
                return answer.answerAsPercentage;
            }

            if (answer.multipleChoiceAnswer !== undefined) {
                return answer.explanation?.trim() ? `${answer.multipleChoiceAnswer}: ${answer.explanation}` : answer.multipleChoiceAnswer;
            }

            if (answer.answerAsDate !== undefined) {
                return moment(answer.answerAsDate).format('DD-MM-YYYY');
            }
        }
        return '?'; // TODO Do not forget to remove this default answer one day
    }

    private updateDerivedFields(): void {
        if (this.project) {
            this.profitContribution = FormatEuroCentsDirective.convertNumberToEuros(this.project.projectInfo?.profitContribution, 'nl');

            this.imageBase64 = this.project.projectInfo.imageBase64 ?
                {previewSrc: this.project.projectInfo.imageBase64, file: null} : null;

            this.project.projectInfo.workStartsAt = this.startDate ? moment(this.startDate).format('DD-MM-YYYY') : null;
            this.project.projectInfo.workEndsAt = this.endDate ? moment(this.endDate).format('DD-MM-YYYY') : null;

            this.termsAndConditions = this.translateProjectInfoValue('termsAndConditions');
            this.contractType = this.translateProjectInfoValue('contractType');
        }
    }

    /**
     * Applicable for `termsAndConditions` and `contractType`, where the translation uses the same name as the property.
     */
    private translateProjectInfoValue(property: string) {
        if (this.project) {
            const value = this.project.projectInfo?.[property];
            if (value === null) {
                return null;
            }

            const key = `PAGE_PROJECT_DETAILS.${property}.${value}`;
            let label = this.translateService.instant(key);

            if (label === key) {
                label = this.translateService.instant(`PAGE_PROJECT_DETAILS.${property}.other`)
            }
            return label;
        }
        return undefined;
    }
}
